// FILE: /src/Components/AccountSetting.js (FULLY UPDATED)

import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux"; // <-- Import useSelector
import { message, Modal, Spin, Button, Tabs, Input } from "antd";
import { UserOutlined, WalletOutlined, ShoppingCartOutlined, DatabaseOutlined, ThunderboltOutlined, SyncOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import { changePassword, deleteAccount, sendDeleteOtp } from "../services/account";
import { fetchUserProfile, cancelSubscription, fetchBillingDetails, fetchAddonBundles, createAddonOrder, checkUserSubscriptionStatus } from "../services/user.profile";
import moment from "moment";
import axiosServices from "../services/axios";
import { useNavigate, useLocation } from "react-router-dom";
import { formatBytes } from '../utils/formatters';
import { updateUserPlanStatus, updateSubscriptionStatus, setSubscription } from "../store/Slices/ProfileSlice";

const convertInrToUsd = (inrAmount) => {
    if (!inrAmount || isNaN(inrAmount)) return '0.00';
    const usdPrice = (inrAmount / 100) / 83;
    return usdPrice.toFixed(2);
};

const UsageMetric = ({ label, used, limit }) => {
const percentage = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
const isExceeded = used > limit;
return (
<div className="bg-gray-50 p-4 rounded-lg border">
<div className="flex justify-between items-baseline mb-2">
<p className="text-sm font-medium text-gray-600">{label}</p>
<p className="text-sm font-semibold text-gray-800">
{used.toLocaleString()} / <span className="text-gray-500">{limit > 0 ? limit.toLocaleString() : 'Unlimited'}</span>
</p>
</div>
<div className="w-full bg-gray-200 rounded-full h-2">
<div className={`h-2 rounded-full transition-all duration-500 ${isExceeded ? 'bg-red-500' : 'bg-brand-primary'}`} style={{ width: `${percentage}%` }}></div>
</div>
</div>
);
};

const StorageMetric = ({ label, usedBytes, limitBytes }) => {
const percentage = limitBytes > 0 ? Math.min((usedBytes / limitBytes) * 100, 100) : 0;
const isExceeded = usedBytes > limitBytes;
return (
<div className="bg-gray-50 p-4 rounded-lg border">
<div className="flex justify-between items-baseline mb-2">
<p className="text-sm font-medium text-gray-600">{label}</p>
<p className="text-sm font-semibold text-gray-800">
{formatBytes(usedBytes, 2)} / <span className="text-gray-500">{limitBytes > 0 ? formatBytes(limitBytes, 2) : 'Unlimited'}</span>
</p>
</div>
<div className="w-full bg-gray-200 rounded-full h-2">
<div className={`h-2 rounded-full transition-all duration-500 ${isExceeded ? 'bg-red-500' : 'bg-brand-primary'}`} style={{ width: `${percentage}%` }}></div>
</div>
</div>
);
};

const AddonStore = ({ onPurchase }) => {
const [bundles, setBundles] = useState([]);
const [loading, setLoading] = useState(true);
const [purchaseLoading, setPurchaseLoading] = useState(null);

useEffect(() => {
const loadBundles = async () => {
try {
const fetchedBundles = await fetchAddonBundles();
setBundles(fetchedBundles);
} catch (error) {
message.error("Could not load add-on bundles.");
} finally {
setLoading(false);
}
};
loadBundles();
}, []);

const handlePurchase = async (bundleId) => {
setPurchaseLoading(bundleId);
await onPurchase(bundleId);
setPurchaseLoading(null);
};

if (loading) return <div className="text-center p-8"><Spin /></div>;
if (bundles.length === 0) return null;

return (
<div className="bg-white rounded-lg p-6 border shadow-sm">
<h3 className="font-semibold mb-4 text-lg flex items-center gap-2"><ShoppingCartOutlined /> One-Time Add-ons</h3>
<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
{bundles.map(bundle => (
<div key={bundle.bundleId} className="border rounded-lg p-5 flex flex-col items-center text-center bg-gray-50 hover:shadow-md transition-shadow">
<div className="text-4xl mb-3">
{bundle.ai_credits_to_add > 0 && <ThunderboltOutlined className="text-yellow-500" />}
{bundle.storage_gb_to_add > 0 && <DatabaseOutlined className="text-blue-500" />}
</div>
<h4 className="font-bold text-lg">{bundle.name}</h4>
<p className="text-gray-500 text-sm mb-4 flex-grow">{bundle.description}</p>
<div className="text-3xl font-bold my-4">
${convertInrToUsd(bundle.price)} <span className="text-base font-normal text-gray-500">/ one-time</span>
</div>
<Button type="primary" ghost block onClick={() => handlePurchase(bundle.bundleId)} loading={purchaseLoading === bundle.bundleId}>
Buy Now
</Button>
</div>
))}
</div>
</div>
);
};

// --- Main Component ---
export default function AccountSetting() {
const navigate = useNavigate();
const location = useLocation();
const dispatch = useDispatch();

// Use useSelector to get user profile from Redux state
const userProfileData = useSelector(state => state.Profiles[0]);

const [userProfile, setUserProfile] = useState(null);
const [localSubscription, setLocalSubscription] = useState(null);
const [profileLoading, setProfileLoading] = useState(true);

const [billingDetails, setBillingDetails] = useState({
    usage: {},
    limits: {},
    invoices: [],
    upcomingInvoice: null,
});

const [detailsLoading, setDetailsLoading] = useState(true);

const [oldPassword, setOldPassword] = useState("");
const [newPassword, setNewPassword] = useState("");
const [email, setEmail] = useState('');
const [showOtp, setShowOtp] = useState(false);
const [otp, setOtp] = useState('');
const [cancelLoading, setCancelLoading] = useState(false);
const [isCheckingStatus, setIsCheckingStatus] = useState(false);

const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
const [deleteStep, setDeleteStep] = useState(1);
const [deleteOtp, setDeleteOtp] = useState('');
const [deleteLoading, setDeleteLoading] = useState(false);

const fetchAllData = useCallback(async () => {
try {
setDetailsLoading(true);
const { user, subscription: subData } = await fetchUserProfile();
setUserProfile(user);
setLocalSubscription(subData);
setEmail(user.email_id);

const billingData = await fetchBillingDetails();
setBillingDetails(billingData);

} catch (error) {
message.error("Failed to load account data.");
} finally {
setProfileLoading(false);
setDetailsLoading(false);
}
}, []);

useEffect(() => {
fetchAllData();
}, [location.state, fetchAllData]);

const handleCheckStatus = async () => {
setIsCheckingStatus(true);
message.loading({ content: 'Checking subscription status...', key: 'status_check' });
try {
const status = await checkUserSubscriptionStatus();
if (status.isActivePlan && status.subscriptionStatus === 'active') {
dispatch(updateUserPlanStatus({ isActivePlan: status.isActivePlan }));
dispatch(updateSubscriptionStatus({ subscriptionStatus: status.subscriptionStatus }));
setLocalSubscription(prev => ({ ...prev, status: 'active', isPendingPayment: false }));
setUserProfile(prev => ({ ...prev, is_active_plan: true }));
message.success({ content: 'Subscription is active!', key: 'status_check', duration: 2 });
} else {
message.info({ content: 'Payment is still processing. Please wait a few more moments.', key: 'status_check', duration: 3 });
}
} catch (error) {
message.error({ content: 'Could not verify status. Please try again.', key: 'status_check', duration: 3 });
} finally {
setIsCheckingStatus(false);
}
};

const handlePurchaseAddon = async (bundleId) => {
    try {
        const orderDetails = await createAddonOrder(bundleId);
        const options = {
            key: orderDetails.key,
            amount: orderDetails.amount,
            currency: orderDetails.currency,
            name: "Oolook Add-on",
            description: `Purchase of ${bundleId}`,
            order_id: orderDetails.orderId,
            handler: async (response) => {
                message.loading({ content: 'Verifying payment & updating your account...', key: 'verify', duration: 10 });
                
                setTimeout(async () => {
                    try {
                        const { user, subscription: newSubscriptionData } = await fetchUserProfile();
                        setUserProfile(user);
                        setLocalSubscription(newSubscriptionData);
                        dispatch(setSubscription(newSubscriptionData));
                        
                        const newBillingData = await fetchBillingDetails();
                        setBillingDetails(newBillingData);

                        message.success({ content: 'Purchase successful! Your resources have been added.', key: 'verify', duration: 4 });
                    } catch (error) {
                        console.error("Failed to refresh data after purchase:", error);
                        message.error({ content: 'Purchase successful, but failed to refresh display. Please reload the page.', key: 'verify', duration: 5 });
                    }
                }, 8000); 
            },
            prefill: { name: orderDetails.userName, email: orderDetails.userEmail, contact: orderDetails.userMobile },
            theme: { color: "#6D28D9" },
        };
        const rzp = new window.Razorpay(options);
        rzp.open();
    } catch (error) {
        message.error(error.message || "Could not initiate purchase.");
    }
};

const sendOtp = async () => {
try {
await axiosServices.post('/user/send-otp', { email });
message.success('OTP sent to your email');
setShowOtp(true);
} catch (err) {
message.error(err.response?.data?.message || 'Failed to send OTP');
}
};

const verifyOtp = async () => {
try {
await axiosServices.post('/user/verify-email-otp', { email, otp });
message.success('Email updated successfully!');
setShowOtp(false);
fetchAllData();
} catch (err) {
message.error('Invalid OTP');
}
};

const handlePasswordChange = async () => {
try {
await changePassword(oldPassword, newPassword);
message.success("Password changed. Please log in again.");
localStorage.clear();
sessionStorage.clear();
window.location.reload();
} catch (error) {
message.error(error?.response?.data || "Password change failed.");
}
};

const handleCancelSubscription = () => {
    Modal.confirm({
        title: 'Change to Free Plan?',
        content: 'This will immediately cancel your paid subscription and switch you to the Free Plan. Your access to paid features will end right away. Are you sure?',
        okText: 'Yes, Switch to Free',
        okType: 'danger',
        onOk: async () => {
            setCancelLoading(true);
            try {
                const response = await cancelSubscription();
                message.success(response.message || "Your plan has been successfully changed to free.");
                window.location.reload();
            } catch (error) {
                message.error(error.message || "Failed to change plan.");
            } finally {
                setCancelLoading(false);
            }
        },
    });
};

const handleOpenDeleteModal = () => setIsDeleteModalOpen(true);
const handleCloseDeleteModal = () => { setIsDeleteModalOpen(false); setDeleteLoading(false); setDeleteStep(1); setDeleteOtp(''); };

const handleSendDeleteOtp = async () => {
setDeleteLoading(true);
try {
await sendDeleteOtp();
message.success("OTP sent to your email.");
setDeleteStep(2);
} catch (error) {
message.error("Failed to send OTP. Please try again.");
} finally {
setDeleteLoading(false);
}
};

const handleConfirmDeletion = async () => {
if (!deleteOtp || deleteOtp.length !== 6) return message.error("Please enter a valid 6-digit OTP.");
setDeleteLoading(true);
try {
await deleteAccount(deleteOtp);
message.success("Account deletion initiated. You will be logged out.");
setTimeout(() => { localStorage.clear(); sessionStorage.clear(); window.location.reload(); }, 2000);
handleCloseDeleteModal();
} catch (error) {
message.error(error.response?.data?.message || "Failed to delete account.");
} finally {
setDeleteLoading(false);
}
};

const tabItems = [
{
key: 'billing',
label: (
<span className="flex items-center gap-2"><WalletOutlined /> Subscription & Billing</span>
),
children: (
detailsLoading ? <div className="text-center p-20"><Spin /></div> :
<div className="space-y-8">
{localSubscription?.isPendingPayment ? (
<div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg shadow-md text-center">
<p className="text-lg font-semibold text-yellow-800">Your subscription is pending payment.</p>
<p className="text-yellow-700 mt-2">Please complete the payment process to activate your plan.</p>
<Button type="primary" className="mt-4" icon={<SyncOutlined spin={isCheckingStatus} />} onClick={handleCheckStatus} loading={isCheckingStatus}>Check Payment Status</Button>
</div>
) : (
<div className="grid grid-cols-1 md:grid-cols-2 gap-8">
<div className="bg-white p-6 rounded-lg border shadow-sm">
<h3 className="font-semibold mb-1 text-lg">Current Plan</h3>
<p className="text-xl font-bold text-gray-800">{localSubscription?.plan?.plan_name || 'Free Plan'}</p>
<p className="text-gray-600 mb-4">${convertInrToUsd(localSubscription?.plan?.plan_price || 0)} / {localSubscription?.plan?.plan_interval || 'month'}</p>
<Button block onClick={() => navigate('/select-plan')}>Change Plan</Button>
{localSubscription?.plan_id !== 'free_plan' && <Button block danger type="text" className="mt-2" onClick={handleCancelSubscription} loading={cancelLoading}>Cancel Plan</Button>}
</div>
<div className="bg-white p-6 rounded-lg border shadow-sm">
<h3 className="font-semibold mb-1 text-lg">Next Billing Date</h3>
{billingDetails.upcomingInvoice ? (
<>
<p className="text-xl font-bold text-gray-800">{moment(billingDetails.upcomingInvoice.next_billing_date).format("MMMM Do, YYYY")}</p>
<p className="text-gray-600">You will be charged ${convertInrToUsd(billingDetails.upcomingInvoice.amount)}.</p>
</>
) : <p className="text-gray-500">{localSubscription?.plan_id === 'free_plan' ? 'You are on the Free plan.' : 'No upcoming bill found.'}</p>}
</div>
</div>
)}
<div className="bg-white p-6 rounded-lg border shadow-sm">
<h3 className="font-semibold mb-4 text-lg">Current Monthly Usage</h3>
<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
<UsageMetric label="Brands / Groups" used={billingDetails.usage?.brandCount || 0} limit={billingDetails.limits?.brands || 0} />
<UsageMetric label="Connected Profiles" used={billingDetails.usage?.profileCount || 0} limit={billingDetails.limits?.profiles || 0} />
<UsageMetric label="Scheduled Posts" used={billingDetails.usage?.scheduledPostsCount || 0} limit={billingDetails.limits?.posts || 0} />
<StorageMetric label="Media Library Storage" usedBytes={(billingDetails.usage?.storageGB || 0) * 1024 * 1024 * 1024} limitBytes={(billingDetails.limits?.storageGB || 0) * 1024 * 1024 * 1024} />
<UsageMetric label="AI Credits" used={billingDetails.usage?.aiCreditsConsumed || 0} limit={billingDetails.limits?.aiCreditsTotal || 0} />
</div>
</div>
<AddonStore onPurchase={handlePurchaseAddon} />
<div className="bg-white p-6 rounded-lg border shadow-sm">
<h3 className="font-semibold mb-4 text-lg">Billing History</h3>
{billingDetails.invoices.length > 0 ? (
<div className="max-h-96 overflow-y-auto">
<table className="w-full text-left text-sm">
<thead className="text-xs text-gray-500 uppercase bg-gray-50 sticky top-0">
<tr>
<th className="p-3">Date</th><th className="p-3">Amount</th><th className="p-3">Description</th><th className="p-3 text-right">Invoice</th>
</tr>
</thead>
<tbody>
{billingDetails.invoices.map(invoice => (
<tr key={invoice.id} className="border-b">
<td className="p-3">{moment(invoice.created_at * 1000).format("DD MMM YYYY")}</td>
<td className="p-3 font-semibold">${convertInrToUsd(invoice.amount_paid)}</td>
<td className="p-3">
<span className="capitalize bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">{invoice.status}</span>
</td>
<td className="p-3 text-right">
{invoice.short_url ? <a href={invoice.short_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-semibold">Download</a> : <span className="text-gray-400">--</span>}
</td>
</tr>
))}
</tbody>
</table>
</div>
) : <p className="text-gray-500">No past invoices found.</p>}
</div>
</div>
)
},
{
key: 'profile',
label: (
<span className="flex items-center gap-2"><UserOutlined /> Profile & Security</span>
),
children: (
profileLoading ? <div className="text-center p-20"><Spin /></div> :
<div className="space-y-8">
<div className="bg-white p-6 rounded-lg border shadow-sm">
<h3 className="font-semibold text-lg mb-4">Email Address</h3>
<p className="text-gray-500 mb-4">Your current login email is <b>{userProfile?.email_id}</b></p>
{!showOtp ? (
<div className="flex items-center gap-4">
<Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter new email address" />
<Button onClick={sendOtp} disabled={!email || email === userProfile?.email_id}>Change</Button>
</div>
) : (
<div className="space-y-2">
<p className="text-sm text-green-600">An OTP has been sent to {email}.</p>
<div className="flex items-center gap-4">
<Input value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="6-digit code" />
<Button onClick={() => setShowOtp(false)}>Cancel</Button>
<Button type="primary" onClick={verifyOtp}>Verify</Button>
</div>
</div>
)}
</div>
{/* --- START: THIS IS THE FIX --- */}
{userProfileData?.authProvider === 'email' ? (
    <div className="bg-white p-6 rounded-lg border shadow-sm">
        <h3 className="font-semibold text-lg mb-4">Change Password</h3>
        <div className="space-y-4">
            <Input.Password placeholder="Current Password" onChange={(e) => setOldPassword(e.target.value)} />
            <Input.Password placeholder="New Password" onChange={(e) => setNewPassword(e.target.value)} />
        </div>
        <div className="flex justify-end mt-4">
            <Button type="primary" onClick={handlePasswordChange}>Save Password</Button>
        </div>
    </div>
) : (
    <div className="bg-white p-6 rounded-lg border shadow-sm">
            <h3 className="font-semibold text-lg mb-4">Password</h3>
            <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 p-4 rounded-md">
                <i className="fa-brands fa-google text-blue-600 text-xl"></i>
                <p className="text-sm text-blue-800">You are signed in with Google. To change your password, please do so through your Google account settings.</p>
            </div>
    </div>
)}
{/* --- END: THIS IS THE FIX --- */}
<div className="bg-red-50 border-l-4 border-red-400 p-6 rounded-lg shadow-sm">
<h3 className="font-semibold text-lg text-red-800">Danger Zone</h3>
<div className="flex justify-between items-center mt-2">
<p className="text-red-700">Permanently delete your account and all of its associated data.</p>
<Button danger onClick={handleOpenDeleteModal}>Delete My Account</Button>
</div>
</div>
</div>
),
},
];

return (
<div className="max-w-5xl mx-auto py-10 px-4">
<h1 className="text-3xl font-bold mb-8 text-gray-800">Account & Billing</h1>
<Tabs defaultActiveKey="billing" items={tabItems} size="large" />

<Modal
title={
<div className="flex items-center gap-3">
<ExclamationCircleFilled className="text-red-500" />
<span className="font-semibold">Delete Your Account</span>
</div>
}
open={isDeleteModalOpen}
onCancel={handleCloseDeleteModal}
footer={
deleteStep === 1 ? [
<Button key="cancel" onClick={handleCloseDeleteModal}>Cancel</Button>,
<Button key="send" type="primary" loading={deleteLoading} onClick={handleSendDeleteOtp}>Send OTP</Button>
] : [
<Button key="cancel" onClick={handleCloseDeleteModal}>Cancel</Button>,
<Button key="delete" type="primary" danger loading={deleteLoading} onClick={handleConfirmDeletion}>Confirm Deletion</Button>
]
}
destroyOnClose
>
{deleteStep === 1 ? (
<div>
<p>This action is permanent and cannot be undone. To confirm, please click "Send OTP" to receive a verification code at your email address.</p>
</div>
) : (
<div className="space-y-4">
<p>An OTP has been sent to your email. Please enter it below to permanently delete your account. The code will expire in 5 minutes.</p>
<Input
placeholder="Enter 6-digit OTP"
value={deleteOtp}
onChange={(e) => setDeleteOtp(e.target.value)}
maxLength={6}
/>
</div>
)}
</Modal>
</div>
);
}